import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useCookies } from 'react-cookie';

import { ErrorDialog } from '../../../Login/ErrorDialog';
import { LocationDialog } from '../../../Login/LocationDialog';
import { LoadingPage } from '../../../LoadingPage';
import { LoginTermsOfUse } from '../../../LoginTermsOfUse';
import { LoginTermOfAuthorization } from '../../../LoginTermOfAuthorization';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import {
    cpfMask,
    dateMask,
    formatName,
    formatToYYYYMMDD,
    isDate,
    isNaturalDocumentNumber,
} from '../../../../utils/utils';
import useInput from '../../../../utils/hooks/useInput';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField, CheckboxFieldStyles as CS } from '../components/CheckboxField';
import { ThemedInput } from '../components/ThemedInput';
import { CreditCard } from '../components/CreditCard';
import CheckIcon from '../SocialSecurityCards/CheckIcon';
import * as S from './styles';

const SKIP_WORDS = new Set(['da', 'de', 'do', 'das', 'dos', 'des', 'e', 'a', 'o']);

const formatCardName = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return name;
    const first = parts[0];
    const last = parts[parts.length - 1];
    const middle = parts
        .slice(1, -1)
        .filter((p) => !SKIP_WORDS.has(p.toLowerCase()))
        .map((p) => p[0].toUpperCase());
    return [first, ...middle, last].join(' ');
};

const PromoScene = ({
    accentColor,
    secondaryColor,
    clientThemeSettings,
    holderName,
    onAccept,
}: {
    accentColor: string;
    secondaryColor: string;
    clientThemeSettings: any;
    holderName: string;
    onAccept: () => void;
}) => {
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();

    return (
        <>
            <S.ScrollContent>
                <S.ProposalHeader $backgroundColor={clientThemeSettings?.background_color} $padBottom={24}>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings?.monochromatic_logo_url ?? clientThemeSettings.logo_url} alt="logo" />
                    )}
                    <S.HeaderTitle>
                        Você possui um <strong>cartão benefício consignado</strong> disponível.
                    </S.HeaderTitle>
                    <S.CardWrapper $color={secondaryColor}>
                        <S.GlassyBadge $color={secondaryColor}>
                            <CheckIcon color={secondaryColor} />
                        </S.GlassyBadge>
                        <CreditCard
                            primaryColor={accentColor}
                            secondaryColor={secondaryColor}
                            label="Benefício"
                            lastDigits="4897"
                            holderName={holderName}
                        />
                    </S.CardWrapper>
                    <S.HeaderBodyText>
                        O cartão benefício funciona como um <strong>cartão de crédito convencional</strong>,
                        e o pagamento da fatura é descontado direto do seu benefício do INSS.
                    </S.HeaderBodyText>
                </S.ProposalHeader>

                <S.AdvantagesSection>
                    <S.AdvantagesTitle $accentColor={accentColor} style={{ textAlign: 'center', width: '100%' }}>
                        Vantagens do cartão
                    </S.AdvantagesTitle>
                    <S.AdvantageHighlight
                        $borderColor={clientThemeSettings.background_color}
                        $textColor={clientThemeSettings.font_color}
                    >
                        Saque de <strong>70%</strong> do valor do seu limite
                    </S.AdvantageHighlight>
                    <S.AdvantagesGrid>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Seguro de vida</S.AdvantageChip>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Auxílio funeral</S.AdvantageChip>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Descontos em farmácias</S.AdvantageChip>
                    </S.AdvantagesGrid>
                </S.AdvantagesSection>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.FooterSection>
                <Button clientThemeSettings={clientThemeSettings} onClick={hasScrolledToEnd ? onAccept : scrollToBottom}>
                    Quero contratar
                </Button>
                <PoweredByQITech />
            </S.FooterSection>
        </>
    );
};

interface LoginProps {
    setSignData: (data: any) => void;
    onSendOTPCode: (token: string) => void;
    setSignerStep: (step: string) => void;
    signData: any;
    setCachedDocumentNumber: (v: string) => void;
    setCachedBirthdate: (v: string) => void;
    appTheme?: string;
}

type VisibleTerm = 'terms_of_use' | 'term_of_authorization' | null;

export const SocialSecurityBenefitCardLogin = (props: LoginProps) => {
    const { clientThemeSettings } = useThemeContext();
    const {
        setSignData,
        onSendOTPCode,
        setSignerStep,
        signData,
        setCachedDocumentNumber,
        setCachedBirthdate,
    } = props;

    const [sceneIndex, setSceneIndex] = useState(0);

    const { signerHash } = useParams<{ signerHash: string }>();
    const [cookies, setCookie] = useCookies([`qisign_${signerHash}`]);
    const hasMadeRequestRef = useRef(false);

    const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);
    const [termOfAuthorizationCheckboxChecked, setTermOfAuthorizationCheckboxChecked] = useState(false);
    const [termsOfUseCheckboxChecked, setTermsOfUseCheckboxChecked] = useState(false);
    const [visibleTerm, setVisibleTerm] = useState<VisibleTerm>(null);
    const [loading, setLoading] = useState(false);
    const [loadingForm, setLoadingForm] = useState(false);
    const [openErrorDialog, setOpenErrorDialog] = useState('');
    const [openLocationDialog, setOpenLocationDialog] = useState(false);
    const [geolocationFailureRequestCount, setGeolocationFailureRequestCount] = useState(0);

    const documentNumberQueryParam = new URLSearchParams(window.location.search).get('document_number');
    const birthdateQueryParam = new URLSearchParams(window.location.search).get('birthdate');
    const nameQueryParam = new URLSearchParams(window.location.search).get('name');
    const loginHashQueryParam = new URLSearchParams(window.location.search).get('login_hash');

    const {
        value: documentNumberValue,
        isValid: documentNumberIsValid,
        hasError: documentNumberHasError,
        valueChangeHandler: documentNumberValueChangeHandler,
        inputBlurHandler: documentNumberInputBlurHandler,
        reset: documentNumberReset,
    } = useInput(isNaturalDocumentNumber);

    const {
        value: birthdateValue,
        isValid: birthdateIsValid,
        hasError: birthdateHasError,
        valueChangeHandler: birthdateValueChangeHandler,
        inputBlurHandler: birthdateInputBlurHandler,
        reset: birthdateReset,
    } = useInput(isDate);

    const resetQueryParams = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('document_number');
        url.searchParams.delete('birthdate');
        url.searchParams.delete('name');
        url.searchParams.delete('login_hash');
        window.history.replaceState({}, document.title, url.toString());
    };

    const collectGeoLocation = useCallback(async () => {
        if (navigator.geolocation) {
            try {
                const position: any = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject);
                });
                return {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
            } catch (error) {
                setGeolocationFailureRequestCount((c) => c + 1);
                return { latitude: null, longitude: null };
            }
        }
        return { latitude: null, longitude: null };
    }, []);

    const handleSendOTPCode = useCallback(
        async (responseData: any) => {
            if (
                responseData.use_authentication_code &&
                !responseData.skip_initial_notification &&
                !cookies[`qisign_${signerHash}`]
            ) {
                onSendOTPCode(responseData.signer_token);
            }
        },
        [onSendOTPCode, signerHash, cookies],
    );

    const isValidInputData = useCallback(
        (checkboxChecked?: boolean) => {
            const effectiveCheckbox = checkboxChecked ?? termsCheckboxChecked;

            if (cookies[`qisign_${signerHash}`]) {
                setOpenErrorDialog('');
                return true;
            }

            if (!effectiveCheckbox && !documentNumberQueryParam) {
                setOpenErrorDialog('terms_not_checked');
                return false;
            }

            if (signData?.signature_settings?.document_number_required) {
                if (!documentNumberIsValid || !birthdateIsValid) {
                    setOpenErrorDialog('invalid_data');
                    return false;
                }
            }

            setOpenErrorDialog('');
            return true;
        },
        [
            birthdateIsValid,
            documentNumberIsValid,
            termsCheckboxChecked,
            documentNumberQueryParam,
            signData,
            cookies,
            signerHash,
        ],
    );

    const onSubmit = useCallback(
        async (
            documentNumber: string,
            birthdate: string,
            name: string | null,
            alreadyFormatted: boolean,
            latitude: number | null,
            longitude: number | null,
            browserKey: string | null,
        ) => {
            const payload: any = {};

            if (documentNumber) {
                payload['document_number'] = alreadyFormatted ? documentNumber : cpfMask(documentNumber);
            }
            if (browserKey) {
                payload['device_data'] = { device_key: browserKey };
            }
            if (birthdate) {
                payload['birthdate'] = alreadyFormatted ? birthdate : formatToYYYYMMDD(birthdate);
            }
            if (name !== null && name !== '') {
                payload['name'] = formatName(name);
            }
            if (latitude && longitude) {
                payload['gps_data'] = { coordinates: [longitude, latitude] };
            }

            axios
                .post('/signer/hash/' + signerHash, payload)
                .then(async (response) => {
                    const session_id = uuidv4();
                    setSignData({ ...signData, ...response.data, session_id });
                    await handleSendOTPCode(response.data);
                    setOpenLocationDialog(false);
                    documentNumberReset();
                    birthdateReset();
                    setCookie(`qisign_${signerHash}`, payload, {
                        path: `/s/${signerHash}`,
                        maxAge: 60 * 60,
                    });
                    setSignerStep(response.data.next_step);
                    setCachedDocumentNumber(payload.document_number);
                    setCachedBirthdate(payload.birthdate);
                })
                .catch((error) => {
                    if ([401, 403, 409].includes(error.response?.status)) {
                        setOpenLocationDialog(false);
                        setOpenErrorDialog('incorrect_personal_data');
                    }
                    setCookie(`qisign_${signerHash}`, '', { path: `/s/${signerHash}`, maxAge: 0 });
                    setLoading(false);
                })
                .finally(() => {
                    setLoadingForm(false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setCookie, setSignData, handleSendOTPCode, birthdateReset, documentNumberReset, signerHash, signData, setSignerStep],
    );

    const executeSubmission = useCallback(
        async (
            docNum?: string,
            bdate?: string,
            name?: string,
            alreadyFormatted = false,
        ) => {
            let browserKey: string | null = null;
            try {
                browserKey = await getOrCreateBrowserKey();
            } catch (_) {}

            let latitude: number | null = null;
            let longitude: number | null = null;

            if (docNum) {
                setLoading(true);
                if (
                    signData?.signature_settings &&
                    !signData.signature_settings?.prevent_geo_location_request &&
                    signData.signature_settings?.geo_location_required
                ) {
                    const geo = await collectGeoLocation();
                    latitude = geo.latitude;
                    longitude = geo.longitude;
                    if (!latitude && !longitude) {
                        setLoading(false);
                        setOpenLocationDialog(true);
                        return;
                    }
                }
                onSubmit(docNum, bdate ?? '', name ?? null, alreadyFormatted, latitude, longitude, browserKey);
            } else {
                if (!isValidInputData()) return;
                setLoadingForm(true);
                if (
                    !signData?.signature_settings?.prevent_geo_location_request &&
                    signData?.signature_settings?.geo_location_required
                ) {
                    const geo = await collectGeoLocation();
                    latitude = geo.latitude;
                    longitude = geo.longitude;
                    if (!latitude && !longitude) {
                        setLoadingForm(false);
                        setOpenLocationDialog(true);
                        return;
                    }
                }
                onSubmit(documentNumberValue, birthdateValue, null, alreadyFormatted, latitude, longitude, browserKey);
            }
        },
        [signData, documentNumberValue, birthdateValue, isValidInputData, collectGeoLocation, onSubmit],
    );

    useEffect(() => {
        if (hasMadeRequestRef.current) return;
        hasMadeRequestRef.current = true;

        if (loginHashQueryParam) {
            if (!signData.signer_data?.document_number) return;
            const handleHashFlow = async () => {
                const keyToInherit = signData.device_data?.device_key ?? null;
                try {
                    await getOrCreateBrowserKey(keyToInherit);
                } catch (_) {}
                executeSubmission(
                    signData.signer_data.document_number,
                    signData.signer_data.birthdate,
                    signData.signer_data.name,
                    true,
                );
                resetQueryParams();
            };
            handleHashFlow();
            return;
        }

        if (documentNumberQueryParam) {
            executeSubmission(documentNumberQueryParam, birthdateQueryParam ?? undefined, nameQueryParam ?? undefined, false);
            resetQueryParams();
            return;
        }

        if (
            cookies[`qisign_${signerHash}`] &&
            isNaturalDocumentNumber(cookies[`qisign_${signerHash}`].document_number)
        ) {
            executeSubmission(
                cookies[`qisign_${signerHash}`].document_number,
                cookies[`qisign_${signerHash}`].birthdate,
                undefined,
                true,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTermsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTermsCheckboxChecked(e.target.checked);
    };

    const handleLocationDialogClose = () => {
        setOpenLocationDialog(false);
        setLoadingForm(false);
        setLoading(false);
    };

    const labelColor = clientThemeSettings?.font_color;
    const accentColor = clientThemeSettings?.font_color || '#0e3573';
    const secondaryColor = clientThemeSettings?.button_color || accentColor;
    const holderName = formatCardName(signData?.signer_data?.name ?? '');

    const isTermsOfUseRequired = signData?.signature_settings?.login_terms?.terms_of_use?.enabled;
    const isTermOfAuthorizationRequired = signData?.signature_settings?.login_terms?.term_of_authorization?.enabled;

    const allFieldsValid = documentNumberIsValid && birthdateIsValid;
    const allCheckboxesChecked =
        termsCheckboxChecked &&
        (!isTermsOfUseRequired || termsOfUseCheckboxChecked) &&
        (!isTermOfAuthorizationRequired || termOfAuthorizationCheckboxChecked);

    const isSubmitDisabled = !allFieldsValid || !allCheckboxesChecked || loadingForm;

    const handleSetVisibleTerm = (term: string) => setVisibleTerm(term as VisibleTerm);

    if (visibleTerm === 'terms_of_use') {
        return <LoginTermsOfUse setVisibleTerm={handleSetVisibleTerm} />;
    }
    if (visibleTerm === 'term_of_authorization') {
        return (
            <LoginTermOfAuthorization
                setVisibleTerm={handleSetVisibleTerm}
                type={signData?.signature_settings?.login_terms?.term_of_authorization?.type}
            />
        );
    }
    if (loading) {
        return <LoadingPage />;
    }

    return (
        <S.PageWrapper>
            {sceneIndex === 0 ? (
                <PromoScene
                    accentColor={accentColor}
                    secondaryColor={secondaryColor}
                    clientThemeSettings={clientThemeSettings}
                    holderName={holderName}
                    onAccept={() => setSceneIndex(1)}
                />
            ) : (
                <>
                    <S.ScrollContent>
                        <S.TopSection>
                            <S.LogoContainer>
                                {clientThemeSettings?.logo_url && (
                                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                                )}
                            </S.LogoContainer>

                            <S.IntroSection>
                                <S.Title $color={labelColor}>
                                    {signData?.signature_settings?.login_content?.title
                                        ? signData.signature_settings.login_content.title
                                        : <>Inicie a contratação<br />do seu <strong>cartão benefício consignado</strong></>
                                    }
                                </S.Title>
                                <S.Subtitle>
                                    {signData?.signature_settings?.login_content?.text ?? 'Insira seus dados para prosseguir:'}
                                </S.Subtitle>
                            </S.IntroSection>

                            <S.FormSection>
                                <S.FormFieldsWrapper
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter') executeSubmission();
                                    }}
                                >
                                    <ThemedInput
                                        labelValue="Número do CPF"
                                        labelColor={labelColor}
                                        onChange={documentNumberValueChangeHandler}
                                        onBlur={documentNumberInputBlurHandler}
                                        hasError={documentNumberHasError}
                                        fieldValue={cpfMask(documentNumberValue)}
                                        errorMessage="Número de CPF inválido."
                                        type="numeric"
                                    />
                                    <ThemedInput
                                        labelValue="Data de nascimento (DD/MM/AAAA)"
                                        labelColor={labelColor}
                                        onChange={birthdateValueChangeHandler}
                                        onBlur={birthdateInputBlurHandler}
                                        hasError={birthdateHasError}
                                        fieldValue={dateMask(birthdateValue)}
                                        errorMessage="Data de nascimento inválida."
                                        type="numeric"
                                    />
                                </S.FormFieldsWrapper>

                                <S.CheckboxGroup>
                                    <CheckboxField
                                        id="qisign-terms"
                                        checked={termsCheckboxChecked}
                                        onChange={handleTermsCheckboxChange}
                                        accentColor={labelColor}
                                    >
                                        Concordo com os{' '}
                                        <a href="https://sign.qitech.com.br/docs/termos_de_uso.pdf" target="_blank" rel="noreferrer" style={{ color: labelColor }}>
                                            Termos de Uso
                                        </a>{' '}
                                        e{' '}
                                        <a href="https://sign.qitech.com.br/docs/politica_de_privacidade.pdf" target="_blank" rel="noreferrer" style={{ color: labelColor }}>
                                            Política de Privacidade
                                        </a>
                                        {' '}do QI Sign.
                                    </CheckboxField>

                                    {isTermsOfUseRequired && (
                                        <CheckboxField
                                            id="qisign-terms-of-use"
                                            checked={termsOfUseCheckboxChecked}
                                            onChange={(e) => setTermsOfUseCheckboxChecked(e.target.checked)}
                                            accentColor={labelColor}
                                        >
                                            Concordo com o{' '}
                                            <CS.PseudoLink $color={labelColor} onClick={() => setVisibleTerm('terms_of_use')}>
                                                Termo de Uso
                                            </CS.PseudoLink>
                                            {' '}da QI SCD.
                                        </CheckboxField>
                                    )}

                                    {isTermOfAuthorizationRequired && (
                                        <CheckboxField
                                            id="qisign-term-of-authorization"
                                            checked={termOfAuthorizationCheckboxChecked}
                                            onChange={(e) => setTermOfAuthorizationCheckboxChecked(e.target.checked)}
                                            accentColor={labelColor}
                                        >
                                            Autorizo o{' '}
                                            <CS.PseudoLink $color={labelColor} onClick={() => setVisibleTerm('term_of_authorization')}>
                                                compartilhamento de dados com Dataprev
                                            </CS.PseudoLink>
                                            .
                                        </CheckboxField>
                                    )}
                                </S.CheckboxGroup>
                            </S.FormSection>
                        </S.TopSection>
                    </S.ScrollContent>

                    <S.FooterSection>
                        <Button
                            clientThemeSettings={clientThemeSettings}
                            disabled={isSubmitDisabled}
                            loading={loadingForm}
                            onClick={executeSubmission}
                        >
                            {loadingForm ? 'Carregando...' : 'Continuar'}
                        </Button>
                        <PoweredByQITech />
                    </S.FooterSection>
                </>
            )}

            {openLocationDialog && (
                <LocationDialog
                    open={openLocationDialog}
                    onClose={handleLocationDialogClose}
                    onSubmit={handleLocationDialogClose}
                    geolocationFailureRequestCount={geolocationFailureRequestCount}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
            {openErrorDialog && (
                <ErrorDialog
                    description={openErrorDialog}
                    onClose={() => setOpenErrorDialog('')}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
        </S.PageWrapper>
    );
};
